import Image from 'next/image'
import {FC} from 'react'

import {BreadcrumbStep} from '@/app/lib/structuredData'
import {Breadcrumbs} from '@/components/common/content/Breadcrumbs'
import {MostRecentArticles} from '@/components/common/recent-articles/MostRecentArticles'
import {Tile} from '@/components/common/tiles/Tile'
import {ServiceRequestTiles} from '@/components/service-request/ServiceRequestTiles'
import {getClassAssistContent} from '@/content/classAssist'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import type {
  ClassAssistIndustryOverview,
  ClassAssistSectorOverview,
  PopularIndustriesSector,
} from '@/utils/class-assist/types'
import {isPartiallySupportedLocale} from '@/utils/constants'
import {RecentArticleCard} from '@/utils/contentful'
import {useNavigation} from '@/utils/hooks/useNavigation'
import {useLanguage} from '@/utils/i18n/language'
import {routeTo} from '@/utils/router'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'

import {GetStartedWithVerificationBanner} from '../../common/banner/GetStartedWithVerificationBanner'
import {Section} from '../../common/content/Section'
import {Hero} from '../../common/hero/Hero'
import {Page} from '../../Page'
import {ClassAssistEstimatedPrice} from '../overview/ClassAssistEstimatedPrice'
import {ClassAssistSelection} from '../selection/ClassAssistSelection'
import {ClassAssistAllClasses} from './ClassAssistAllClasses'
import {ClassAssistPopularSectors} from './ClassAssistPopularSectors'
import {ClassAssistRecommendedClasses} from './ClassAssistRecommendedClasses'

type ClassAssistIndustryPageProps = {
  breadcrumbTrail: BreadcrumbStep[] | null
  articles: RecentArticleCard[]
  industry: null | ClassAssistIndustryOverview
  sector: null | ClassAssistSectorOverview
  industryOptions: {value: string; label: string}[]
  sectorOptions: {value: string; label: string}[]
  popularSectors: PopularIndustriesSector[]
}

export const ClassAssistIndustryPage: FC<ClassAssistIndustryPageProps> = ({
  breadcrumbTrail,
  articles,
  industry,
  sector,
  industryOptions,
  sectorOptions,
  popularSectors,
}) => {
  const language = useLanguage()
  const {t} = useTranslation()
  const {headerLabel, header, hero, tiles, getStartedBanner, relatedArticlesTitle} =
    getClassAssistContent(language)
  const router = useNavigation()

  const heroTitleByPage =
    sector !== null
      ? t(hero.industryTitle, {name: sector.pageName})
      : industry !== null
      ? t(hero.industryTitle, {name: industry.name})
      : t(hero.title)

  const heroSubtitleByPage =
    sector !== null
      ? t(hero.industrySubtitle, {name: sector.pageName})
      : industry !== null
      ? t(hero.industrySubtitle, {name: industry.name})
      : t(hero.subtitle)

  const isPartiallySupported = isPartiallySupportedLocale(language)

  return (
    <>
      <Page
        navigationLabel={isPartiallySupported ? t(headerLabel) : undefined}
        hasGetHelpButton={!isPartiallySupported}
        hasRegionalSettings={!isPartiallySupported}
        hasLogin={!isPartiallySupported}
        mainCtaButton={{
          text: t(header.buttonLinkText),
          linkHref: header.buttonLinkHref,
          onClick: () =>
            analytics.track('Click on Start registration (Class assist - Header)', {
              industry: industry ? industry.name : '',
              sector: sector ? sector.name : '',
            }),
        }}
      >
        <Hero
          title={heroTitleByPage}
          subtitle={heroSubtitleByPage}
          breadcrumbs={breadcrumbTrail && <Breadcrumbs trail={breadcrumbTrail} hideCurrent />}
        >
          <div className="hero-image">
            <Image src={hero.imageSrc} alt={hero.imageAlt} sizes="370px" fill priority />
          </div>
        </Hero>
        <Section hasPaddingTop>
          <ClassAssistSelection
            industryOptions={industryOptions}
            industryValue={industry?.slug ?? null}
            onIndustryChange={(e) => {
              if (industry?.slug !== e.value) {
                router.push(routeTo.classAssist.industry(e.value), {scroll: false})
              }
            }}
            sectorOptions={sectorOptions}
            sectorValue={sector?.slug ?? null}
            onSectorChange={(e) => {
              if (industry && e.value) {
                router.push(routeTo.classAssist.industry(industry.slug, e.value), {
                  scroll: false,
                })
              }
            }}
          >
            {industry != null && sector != null && (
              <>
                <ClassAssistRecommendedClasses sectorName={sector.name} classes={sector.classes} />
                <ClassAssistEstimatedPrice
                  sectorName={sector.name}
                  numOfClasses={sector.classes?.length ?? 1}
                />
              </>
            )}
          </ClassAssistSelection>
        </Section>
        {articles?.length > 0 ? (
          <Section hasPaddingTop>
            <MostRecentArticles
              articlesTitle={t(relatedArticlesTitle)}
              articles={articles}
              trackedEvent={{
                name: 'RelatedBlogs-ClassAssistIndustry',
                parameters: {
                  industry: industry ? industry.name : '',
                  sector: sector ? sector.name : '',
                },
              }}
            />
          </Section>
        ) : null}
        <Section hasPaddingTop>
          <ServiceRequestTiles title={t(tiles.title)}>
            {tiles.list.map(({title, description, imageSrc, imageAlt}, i) => (
              <Tile
                key={i}
                title={t(title)}
                description={t(description)}
                imageSrc={imageSrc}
                imageAlt={imageAlt}
              />
            ))}
          </ServiceRequestTiles>
        </Section>
        {industry === null && (
          <Section hasPaddingTop>
            <ClassAssistAllClasses />
          </Section>
        )}
        <Section hasPaddingTop>
          <ClassAssistPopularSectors popularSectors={popularSectors} />
        </Section>
        <GetStartedWithVerificationBanner
          title={t(getStartedBanner.title)}
          primaryButtonLink={getStartedBanner.buttonLink}
          primaryButtonText={t(getStartedBanner.buttonText)}
          primaryButtonTrackedEvent="GetStartedBanner-ClassAssist"
          hasPaddingTop
        />
      </Page>

      <style jsx>{`
        .hero-image {
          position: relative;
          flex: none;
          width: 370px;
          aspect-ratio: 370 / 330;
          margin-left: ${theme.spacing[6]};
        }
        @media ${TABLET_SCREENSIZE} {
          .hero-image {
            margin-left: 0;
            margin-top: ${theme.spacing[6]};
          }
        }
        @media ${MOBILE_SCREENSIZE} {
          .hero-image {
            width: 100%;
            max-width: 370px;
            margin-top: ${theme.spacing[5]};
          }
        }
      `}</style>
    </>
  )
}
